<template>
  <!--
    Shared, presentational order-summary card for the list/grid views
    (OrdersList.vue and OnlineOrders.vue). Phase 5 extraction of the
    ~50-line per-order card that was duplicated (with only cosmetic class
    differences) between those two views.

    This component is intentionally "dumb": it performs NO pricing, discount,
    refund or status math. Each parent view computes its own display values
    (amount text, info cells, meta lines) and passes them in props-down, and
    supplies the divergent pills / action buttons via the named slots. Card
    selection is reported events-up via `card-click` so each view keeps its
    own selection behavior (OrdersList -> viewOrder, OnlineOrders ->
    currentOrder = order).

    Class names are passed through the `classes` prop so each view keeps its
    EXACT existing CSS (which lives, unchanged, in the parent view's global
    <style> block). Defaults match OrdersList's `ol-*` markup.
  -->
  <div
    class="text-left"
    :class="[mergedClasses.card, accentClass, { active: selected }]"
    @click="$emit('card-click', order)"
  >
    <div :class="mergedClasses.header">
      <div :class="mergedClasses.headerLeft">
        <span :class="mergedClasses.id">#{{ idText }}</span>
        <slot name="pills" />
      </div>
      <div
        v-if="customer"
        :class="mergedClasses.customer"
      >
        <strong>{{ customer.first_name }} {{ customer.last_name }}</strong>
        <span v-if="customer.phone"> &middot; {{ customer.phone }}</span>
      </div>
      <div :class="mergedClasses.amount">
        <span :class="mergedClasses.amountLabel">{{ amountLabel }}</span>
        <span :class="mergedClasses.amountValue">{{ currency }}{{ amountText }}</span>
      </div>
    </div>

    <div
      v-for="(meta, mIndex) in metaLines"
      :key="'meta-' + mIndex"
      :class="meta.className"
    >
      {{ meta.text }}
    </div>

    <div :class="mergedClasses.bottom">
      <div :class="mergedClasses.info">
        <div
          v-for="(cell, cIndex) in infoCells"
          :key="'cell-' + cIndex"
          :class="mergedClasses.infoCell"
        >
          <span :class="mergedClasses.infoLabel">{{ cell.label }}</span>
          <span :class="[mergedClasses.infoValue, cell.valueClass]">{{ cell.value }}</span>
        </div>
      </div>
      <div
        v-if="showActions"
        :class="mergedClasses.actions"
        @click.stop
      >
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<script>
// Default class map mirrors OrdersList.vue's existing `ol-*` markup so that
// view keeps identical DOM/CSS without passing a `classes` override.
const DEFAULT_CLASSES = {
  card: "ol-card",
  header: "ol-header",
  headerLeft: "ol-header-left",
  id: "ol-id",
  customer: "ol-customer",
  amount: "ol-amount",
  amountLabel: "ol-amount-label",
  amountValue: "ol-amount-value",
  bottom: "ol-bottom",
  info: "ol-info",
  infoCell: "ol-info-cell",
  infoLabel: "ol-info-label",
  infoValue: "ol-info-value",
  actions: "ol-actions"
};

export default {
  name: "OrderListCard",
  props: {
    // The order object backing this card. Read-only here; never mutated.
    order: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      default: ""
    },
    // Status accent class (e.g. "accent-open"); computed by the parent.
    accentClass: {
      type: String,
      default: ""
    },
    selected: {
      type: Boolean,
      default: false
    },
    // Text shown after the leading "#" in the header.
    idText: {
      type: [String, Number],
      default: ""
    },
    amountLabel: {
      type: String,
      default: "Total"
    },
    // Pre-formatted amount string (currency is prepended in the template).
    amountText: {
      type: String,
      default: "0.00"
    },
    customer: {
      type: Object,
      default: null
    },
    // [{ text, className }] – rendered between the header and the bottom row.
    metaLines: {
      type: Array,
      default: () => []
    },
    // [{ label, value, valueClass? }] – the bottom info grid.
    infoCells: {
      type: Array,
      default: () => []
    },
    // Whether to render the actions wrapper (some statuses show no actions).
    showActions: {
      type: Boolean,
      default: true
    },
    // Optional per-view class-name overrides (see DEFAULT_CLASSES).
    classes: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    mergedClasses () {
      return { ...DEFAULT_CLASSES, ...this.classes };
    }
  }
};
</script>
